import * as React from "react"
import { useTranslation } from "react-i18next"
import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Link2,
  Undo2,
  Redo2,
} from "lucide-react"

import { cn } from "@/lib/utils"

export interface RichTextEditorProps {
  /** Controlled HTML value (sanitized server-side on write + read). */
  value: string
  onChange: (html: string) => void
  ariaLabel: string
  id?: string
  className?: string
  /** Read-only while a save is in flight. Without it this is the ONE control that stays live during a submit, so
   *  keystrokes made in that window are discarded when the dialog closes on success (diff panel). */
  isDisabled?: boolean
}

/**
 * A small rich-text editor over TipTap v3 (ADR-013 §2). StarterKit is **constrained to the sanitizer allowlist** —
 * `heading` levels [2,3], `codeBlock` disabled — so the editor can only ever emit what the server keeps (no `h1`/
 * `h4–h6`/`<pre>` silently stripped). Headless → styled with our tokens (`.rte-content`, base.css) for both themes.
 * Emits HTML via `onChange`; the value is stored in `manual.description` and sanitized at the API boundary.
 */
export function RichTextEditor({ value, onChange, ariaLabel, id, className, isDisabled = false }: RichTextEditorProps) {
  const editor = useEditor({
    immediatelyRender: false, // initialise after mount → SSR-safe + avoids a render-phase flush in tests
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false })],
    content: value,
    editable: !isDisabled,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-label": ariaLabel,
        "aria-multiline": "true",
        class: "rte-content min-h-[7rem] px-3 py-2 text-sm",
        ...(id ? { id } : {}),
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  // `editable` on `useEditor` is the INITIAL value only, so a prop that flips mid-life (a save starting) needs this
  // or the editor stays writable for the whole in-flight window — which is exactly the case this prop exists for.
  // `editor` is null until the init effect runs (`immediatelyRender: false`), hence the guard.
  React.useEffect(() => {
    editor?.setEditable(!isDisabled)
  }, [editor, isDisabled])

  if (!editor) {
    // immediatelyRender:false → the editor initialises in an effect; render a matching shell to avoid layout shift.
    return (
      <div className={cn("border-border rounded-lg border", className)}>
        <div className="border-border/60 h-9 border-b" />
        <div className="min-h-[7rem]" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        // `border-border` (not `border-input` — which equals `--card` in dark → invisible on the drawer surface).
        //
        // **Focus = ONE flush brand line, exactly like `Input`/`Textarea`/`Select`** (arc-conventions §3). This used to
        // be `focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-3` — the shadcn default — which
        // paints a soft 3px halo at 50% opacity over `--ring`. Against the brand red that reads as a thick PINK slab,
        // and it sat directly beneath a Step-name `Input` wearing the correct crisp line: the same form, two different
        // focus treatments. The rule exists precisely because this keeps coming back (owner report, 2026-09-02).
        //
        // NB the app-wide `ring-ring/50` on buttons and list rows is a DIFFERENT, deliberate style — the ban is on
        // FIELDS, which have to match each other.
        "border-border focus-within:border-primary focus-within:ring-1 focus-within:ring-primary rounded-lg border bg-transparent transition-colors",
        className,
      )}
    >
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}

function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation()
  const [linkOpen, setLinkOpen] = React.useState(false)
  const [linkValue, setLinkValue] = React.useState("")
  // Re-render on every transaction so the toolbar's pressed/disabled states track the current selection.
  const [, force] = React.useReducer((x: number) => x + 1, 0)
  React.useEffect(() => {
    editor.on("transaction", force)
    return () => {
      editor.off("transaction", force)
    }
  }, [editor])


  const applyLink = () => {
    const href = linkValue.trim()
    if (href) editor.chain().focus().extendMarkRange("link").setLink({ href }).run()
    else editor.chain().focus().extendMarkRange("link").unsetLink().run()
    setLinkOpen(false)
    setLinkValue("")
  }
  const onLinkButton = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }
    setLinkValue((editor.getAttributes("link").href as string | undefined) ?? "")
    setLinkOpen((o) => !o)
  }

  return (
    <div className="border-border/60 flex flex-col border-b">
      <div className="flex flex-wrap items-center gap-0.5 p-1 [scrollbar-width:none] max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:[&>*]:shrink-0" role="toolbar" aria-label={t("richText.formatting")}>
        <Btn label={t("richText.undo")} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 />
        </Btn>
        <Btn label={t("richText.redo")} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 />
        </Btn>
        <Sep />
        <Btn label={t("richText.bold")} pressed={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold />
        </Btn>
        <Btn label={t("richText.italic")} pressed={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic />
        </Btn>
        <Btn label={t("richText.underline")} pressed={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon />
        </Btn>
        <Btn label={t("richText.strikethrough")} pressed={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough />
        </Btn>
        <Btn label={t("richText.inlineCode")} pressed={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}>
          <Code />
        </Btn>
        <Sep />
        <Btn label={t("richText.heading2")} pressed={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 />
        </Btn>
        <Btn label={t("richText.heading3")} pressed={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 />
        </Btn>
        <Sep />
        <Btn label={t("richText.bulletList")} pressed={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List />
        </Btn>
        <Btn label={t("richText.numberedList")} pressed={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered />
        </Btn>
        <Btn label={t("richText.quote")} pressed={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote />
        </Btn>
        <Sep />
        <Btn label={t("richText.link")} pressed={editor.isActive("link") || linkOpen} onClick={onLinkButton}>
          <Link2 />
        </Btn>
        <Btn label={t("richText.horizontalRule")} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus />
        </Btn>
      </div>
      {linkOpen ? (
        <div className="border-border/60 flex items-center gap-1.5 border-t p-1.5">
          <input
            type="url"
            aria-label={t("richText.linkUrl")}
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                applyLink()
              }
              if (e.key === "Escape") setLinkOpen(false)
            }}
            placeholder="https://…"
            className="border-border focus-visible:border-ring focus-visible:ring-ring/50 h-7 flex-1 rounded-md border bg-transparent px-2 text-sm outline-none focus-visible:ring-3 dark:bg-input/30"
          />
          <button
            type="button"
            onClick={applyLink}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/80 h-7 rounded-md px-2.5 text-[0.8rem] font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            {t("richText.apply")}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function Btn({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string
  pressed?: boolean
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection when pressing a toolbar button
      onClick={onClick}
      className={cn(
        "text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 flex size-7 items-center justify-center rounded-md outline-none transition-colors focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4",
        pressed && "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <span aria-hidden="true" className="bg-border mx-0.5 h-5 w-px" />
}
